import * as React from 'react';
import { connect, Dispatch } from 'react-redux';

import EmailVerification from '../components/EmailVerification';
import {
  logout,
  refreshTokens,
  redirectToConsole,
  verifyEmailAddress
} from '../actions';
import { EmailVerificationData, StoreState } from '../types';
import { getEmailVerificationData } from '../reducers/emailVerification';

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  /**
   * This class maintains some state for the underlying Auth component,
   * and provides callbacks to update this state as well as to log the user
   * in with the appropriate credentials. It also toggles on and off the busy state
   * while authentication is happening.
   */
  class EmailVerificationContainer extends React.Component<
    EmailVerificationContainerProps,
    EmailVerificationContainerState
  > {
    constructor(props: EmailVerificationContainerProps) {
      super(props);
      this.state = {
        busy: false,
        complete: false,
        password: '',
        showPasswordError: false
      };
    }

    render() {
      const { className, emailVerificationData } = this.props;
      return (
        <EmailVerification
          className={className}
          emailVerificationData={emailVerificationData}
          busy={this.state.busy}
          complete={this.state.complete}
          onPasswordInputChange={this.handlePasswordInputChange}
          verifyEmailAddress={this.verifyEmailAddress}
          redirectToLogin={this.redirectToLogin}
          redirectToConsole={this.redirectToConsole}
          password={this.state.password}
          showPasswordError={this.state.showPasswordError}
        />
      );
    }

    handlePasswordInputChange = (
      event: React.SyntheticEvent<HTMLInputElement>
    ) => {
      const value = event.currentTarget.value;
      this.setState({
        ...this.state,
        password: value,
        showPasswordError: false
      });
    };

    redirectToLogin = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();
      this.setState({ busy: true });
      this.props.redirectToLogin();
    };

    redirectToConsole = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();
      this.setState({ busy: true });
      this.props.redirectToConsole();
    };

    /**
     * Starts the verification flow
     */
    verifyEmailAddress = (event: React.SyntheticEvent<HTMLElement>) => {
      event.preventDefault();

      // validate a password was provided
      if (!this.state.password) {
        this.setState({
          ...this.state,
          showPasswordError: true
        });
        return;
      }
      // set our busy state
      this.setState({ ...this.state, busy: true });

      // start the flow
      this.props
        .verifyEmailAddress(
          this.props.emailVerificationData!.previousEmail!,
          this.state.password,
          this.props.emailVerificationData!.changeId!,
          this.props.emailVerificationData!.changeToken!
        )
        .then(success => {
          // update our state to update the view
          this.setState({
            ...this.state,
            complete: success,
            busy: false
          });
        });
    };
  }
);

export interface EmailVerificationContainerProps {
  /** An optional className to override styles */
  className?: string;

  /** Email verification data */
  emailVerificationData?: EmailVerificationData;

  /** Kicks the user back to the login view */
  redirectToLogin(): void;

  /** Kicks the user over to console */
  redirectToConsole(): void;

  /** Handles the email verification flow */
  verifyEmailAddress(
    previousEmail: string,
    password: string,
    changeId: string,
    changeToken: string
  ): Promise<boolean>;
}

export interface EmailVerificationContainerState {
  /** Whether to indicate that the application is processing */
  busy: boolean;

  /** Have we completed the flow? */
  complete: boolean;

  /** User-provided password */
  password: string;

  /** Whether to show inline contextual error message */
  showPasswordError: boolean;
}

function mapStateToProps(
  state: StoreState,
  _ownProps?: Pick<EmailVerificationContainerProps, 'className'>
): Pick<EmailVerificationContainerProps, 'emailVerificationData'> {
  return {
    emailVerificationData: getEmailVerificationData(state)
  };
}

function mapDispatchToProps(
  dispatch: Dispatch<StoreState>
): Pick<
  EmailVerificationContainerProps,
  'redirectToConsole' | 'redirectToLogin' | 'verifyEmailAddress'
> {
  return {
    redirectToConsole() {
      dispatch(refreshTokens()).then(() => dispatch(redirectToConsole()));
    },
    redirectToLogin() {
      dispatch(logout());
    },
    verifyEmailAddress(
      previousEmail: string,
      password: string,
      changeId: string,
      changeToken: string
    ) {
      return dispatch(
        verifyEmailAddress(previousEmail, password, changeId, changeToken)
      );
    }
  };
}
